/**
 * authToken — escolha ÚNICA do token que o SPA envia à API.
 *
 * Contrato de identidade (`docs/infra-contract.md` → "Token e transporte"): a SPA
 * envia `Authorization: Bearer <idToken>`. O authorizer da API verifica
 * `tokenUse: 'id'` (`infra/src/assets/authorizer/proxy.ts`) porque os grupos
 * (`cognito:groups`), o `email` e as claims do trigger PreTokenGeneration vêm no
 * id token. Enviar o access token faz toda rota protegida ser negada.
 *
 * Este módulo é puro (sem dependência de browser/SDK) de propósito: o teste de
 * contrato da infra (`infra/src/test/contrato-token-spa-authorizer.test.ts`)
 * importa ESTA função e passa o token escolhido pelo verificador REAL do
 * authorizer — os dois lados da junta, sem dublê no meio.
 */

import type { CognitoSession } from './cognitoClient';

/** Retorna o JWT que vai no `Authorization: Bearer` das chamadas à API. */
export function tokenParaApi(session: Pick<CognitoSession, 'idToken' | 'accessToken'>): string {
  return session.idToken;
}

/**
 * Claim que o PreTokenGeneration põe no id token do admin sem TOTP cadastrado
 * (ADR-021). Espelha `CLAIM_CADASTRO_MFA_PENDENTE` de
 * `infra/src/config/constants.ts` — o teste de contrato da infra compara os dois.
 */
export const CLAIM_CADASTRO_MFA_PENDENTE = 'mfa_cadastro_pendente';

/** Decodifica o payload de um JWT (sem verificar — a verificação é da API). */
function payloadDoJwt(jwt: string): Record<string, unknown> {
  const partes = jwt.split('.');
  if (partes.length !== 3) {
    return {};
  }
  try {
    const base64 = partes[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      Array.from(atob(base64), (c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''),
    );
    const payload: unknown = JSON.parse(json);
    return payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/**
 * true quando o id token é o token RESTRITO do primeiro acesso do admin: o SPA
 * deve levar ao cadastro do TOTP em vez de abrir o sistema (a API nega o token).
 */
export function cadastroMfaPendente(idToken: string): boolean {
  return payloadDoJwt(idToken)[CLAIM_CADASTRO_MFA_PENDENTE] === 'true';
}

/**
 * Momento (epoch ms) em que o JWT vence, pela claim `exp`; `null` se o token não é um
 * JWT legível (sessões de teste). Usado para renovar a sessão antes de vencer (ADR-031).
 */
export function expiraEmMs(jwt: string): number | null {
  const exp = payloadDoJwt(jwt).exp;
  return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : null;
}
